// ────────────────────────────────────────────────────────────────────
// Axios client + endpoint helpers.
//
// In dev: Vite proxies /api/* → http://127.0.0.1:8000 (see vite.config.ts).
// In prod: FastAPI serves the built portal under /portal/*, so /api is
// same-origin.
//
// Token: JWT stored in localStorage under "xzynexa.token". Cleared on
// 401/403 + logout.
// ────────────────────────────────────────────────────────────────────

import axios, { type AxiosInstance, AxiosError } from "axios";
import type {
  LoginResponse, UserOut, UserUpdate,
  Client, Plant, PlantTriage, PipelineSegment, Area, Asset, AssetSummary, CmlPoint, Inspection, CorrosionData, AssetRbi,
  Entitlements, AssetRecommendation, AssetFinding, AssetPhotoRef, Measurement, InspectionHistoryItem, AssetReview, AssetAssessment, PendingReview,
  SearchOut, ComplianceOut, ComplianceRow, IntakeFile, IntakeDirection,
  IntakeInspect, IntakeImportPreview, IntakeImportMapping, IntakeImportResult, IntakeTemplate,
  RegisterMapping, RegisterPreview, RegisterResult,
  Submission, SubmissionDetail, SubmissionEvent, AppNotification, Conversation, PresenceEntry,
  ReviewPinsOut, ReviewPin, InboxRow,
  Package, PackageCreate, PackagePatch,
  WhatIfBody, WhatIfResult, FocusNote,
  GuestInvite, GuestInviteCreate, GuestRedeemOut,
} from "./types";
// The wire shape of GET /api/assets/{id}/integrity. It lives beside the screen
// that reads it (screens/asset/types.ts) rather than in ./types, because that
// file is the documented mirror of server/integrity/record.py and splitting it
// in two is how the two copies would drift. Type-only import — no runtime
// dependency from lib/ on screens/.
import type { AssetIntegrity } from "@/screens/asset/types";
// The wire shape of GET /api/queue, kept beside its only consumer for the same
// reason as AssetIntegrity above: it is the documented mirror of
// server/schemas.py's Queue* models, and a mirror split across two directories
// is a mirror that drifts. Type-only import.
import type { QueueOut } from "@/screens/queue/types";

const TOKEN_KEY = "xzynexa.token";
const USER_KEY  = "xzynexa.user";
// Staff carry their active tenant from the studios hand-off; customers are
// tenant-locked server-side so this is never set/sent for them.
const TENANT_KEY = "xzynexa.handoff_tenant";
// Set when a staff user clears the tenant DELIBERATELY (the picker's "Clear
// selection"). Without it, "never chose a customer" and "chose to look at
// none" are the same absence of TENANT_KEY, and AppShell — which auto-selects
// a default so the first Atlas fetch is not RLS-scoped to nothing — undid the
// clear on the very reload the button triggers. The button changed nothing
// anyone could see. This key is the user's intent, which is the one thing the
// absence of a value cannot carry.
const TENANT_CLEARED_KEY = "xzynexa.tenant_cleared";
// Guest session — DELIBERATELY a separate key from TOKEN_KEY. A guest link
// opened in one tab must never read/clobber a logged-in staff/customer
// session in another tab (or vice versa) — see guestApi below, which is the
// only axios instance that ever sends this. (docs/superpowers/specs/
// 2026-07-23-guest-access-design.md, frontend PR 2.)
const GUEST_TOKEN_KEY = "xzynexa.guestToken";

// Staff roles route to the studios app; everyone else stays in the portal.
export const STAFF_ROLES = ["admin", "inspector", "engineer"] as const;

// Origin of the staff studios app (single-login hand-off target). Dev default
// = the FastAPI server; override via VITE_STUDIOS_URL. At deploy this becomes
// same-origin and the hand-off can be dropped.
export const STUDIOS_URL: string =
  (import.meta.env.VITE_STUDIOS_URL as string | undefined) || "http://localhost:8000";

/** Build the studios hand-off URL carrying the JWT in the URL fragment.
 *  Fragment (not query) so the token is never sent to a server or logged. */
export function studiosHandoffUrl(token: string): string {
  return `${STUDIOS_URL}/login.html#token=${encodeURIComponent(token)}`;
}

/** Inverse hand-off: if this page was opened from the Console with a token in
 *  the URL fragment (#token=...), adopt it into storage and strip it from the
 *  URL. Lets staff switch Console -> portal (Atlas) without re-login. */
export function adoptHandoffToken(): void {
  try {
    const raw = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1) : window.location.hash;
    const params = new URLSearchParams(raw);
    const t = params.get("token");
    const tenant = params.get("tenant");
    if (t) {
      setToken(t);
      if (tenant) localStorage.setItem(TENANT_KEY, tenant);
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  } catch {
    /* localStorage / history may be unavailable — ignore */
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
  api.defaults.headers.common.Authorization = `Bearer ${token}`;
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TENANT_KEY);
  // Signing out is neither choosing nor clearing — the next person to sign in
  // on this browser must get the ordinary default-tenant treatment, not
  // inherit a previous user's empty desk.
  localStorage.removeItem(TENANT_CLEARED_KEY);
  delete api.defaults.headers.common.Authorization;
}
/** Staff "view as tenant" — the active customer tenant sent as X-Tenant-Id. */
export function getActiveTenant(): string | null {
  return localStorage.getItem(TENANT_KEY);
}
export function setActiveTenant(id: string | number | null): void {
  if (id == null || id === "") {
    localStorage.removeItem(TENANT_KEY);
    localStorage.setItem(TENANT_CLEARED_KEY, "1");
  } else {
    localStorage.setItem(TENANT_KEY, String(id));
    localStorage.removeItem(TENANT_CLEARED_KEY);
  }
}
/** Did the user clear the tenant on purpose, as opposed to never having
 *  chosen one? Only ``setActiveTenant`` writes this, so a hand-off, an inbox
 *  deep link or a chat jump — all of which pass a real id — reset it. */
export function tenantClearedOnPurpose(): boolean {
  return localStorage.getItem(TENANT_CLEARED_KEY) === "1";
}
export function getCachedUser(): UserOut | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as UserOut) : null;
  } catch {
    return null;
  }
}
export function setCachedUser(user: UserOut): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

// ── Guest session (separate from the staff/customer TOKEN_KEY above) ──
export function getGuestToken(): string | null {
  return localStorage.getItem(GUEST_TOKEN_KEY);
}
export function setGuestToken(token: string): void {
  localStorage.setItem(GUEST_TOKEN_KEY, token);
}
export function clearGuestToken(): void {
  localStorage.removeItem(GUEST_TOKEN_KEY);
}

// ── Axios instance ─────────────────────────────────────────────────
export const api: AxiosInstance = axios.create({
  baseURL: "",                     // empty = same origin; vite proxy handles /api
  timeout: 15000,
});

// Attach token from storage on every request (covers page reload)
const existingToken = getToken();
if (existingToken) {
  api.defaults.headers.common.Authorization = `Bearer ${existingToken}`;
}

// Request interceptor — staff viewing the portal (e.g. Atlas via hand-off) send
// their active tenant as X-Tenant-Id so RLS scopes to it. Customers never send
// it (they are locked to their membership tenant server-side).
api.interceptors.request.use((config) => {
  const tenant = localStorage.getItem(TENANT_KEY);
  const user = getCachedUser();
  if (tenant && user && (STAFF_ROLES as readonly string[]).includes(user.role)) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>)["X-Tenant-Id"] = tenant;
  }
  return config;
});

/** Set by the 401 interceptor when an *authenticated* request is rejected,
 *  so the login screen can tell "your session expired" apart from a fresh
 *  sign-in. Read-and-cleared by LoginScreen. */
export const SESSION_EXPIRED_KEY = "xzynexa.session_expired";

// Global response interceptor — auto-logout on 401
api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      const isLoginCall = (err.config?.url ?? "").includes("/api/auth/login");
      const hadToken = !!getToken();
      clearToken();
      // Flag an expiry only when a logged-in session was rejected — NOT on a
      // failed sign-in (no token yet) — so the banner never lies.
      if (hadToken && !isLoginCall) {
        try { sessionStorage.setItem(SESSION_EXPIRED_KEY, "1"); } catch { /* ignore */ }
      }
      // Don't redirect from here — let RequireAuth handle navigation
      // so we don't break HMR or accidentally redirect during login attempt.
    }
    return Promise.reject(err);
  },
);

// ── Guest axios instance ──────────────────────────────────────────
// A guest is NOT a `users` row (guest_auth.py) and its JWT must never be
// confused with a staff/customer session's — a guest tab and a logged-in
// staff tab can be open side by side (or even the SAME browser profile) and
// must not cross-contaminate. `api` above only ever attaches TOKEN_KEY;
// `guestApi` only ever attaches GUEST_TOKEN_KEY. Every guest-reachable call
// (redeem + the guest-scoped reads) goes through this instance, never `api`.
export const guestApi: AxiosInstance = axios.create({
  baseURL: "",
  timeout: 15000,
});
guestApi.interceptors.request.use((config) => {
  const t = getGuestToken();
  if (t) {
    config.headers = config.headers ?? {};
    (config.headers as Record<string, string>).Authorization = `Bearer ${t}`;
  }
  return config;
});

// ── Desk landing (workbench roadmap A2 revised, 2026-08-03 desk-home-redesign) ──
export interface DeskEstate {
  assets: number;
  plants: number;
  overdue: number;
  due_soon: number;
  latest_reading: string | null;
  assets_with_readings: number;
}
export interface DeskRecent {
  id: number;
  tag: string;
  kind: string;
  corrosion_rate_mm_y: number | null;
  remaining_life_y: number | null;
  next_due: string | null;
  state: string;
  last_reading: string | null;
}
export interface DeskSummary { estate: DeskEstate; recent: DeskRecent[] }

// ── Endpoint helpers (typed, thin wrappers) ─────────────────────────

/** OAuth2 password flow — backend expects form-encoded body. */
export async function login(username: string, password: string): Promise<LoginResponse> {
  const form = new URLSearchParams();
  form.set("username", username);
  form.set("password", password);
  const r = await api.post<LoginResponse>("/api/auth/login", form, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  return r.data;
}

export const apiClient = {
  // ── Auth ──
  login,
  me: () => api.get<UserOut>("/api/auth/me").then((r) => r.data),
  listUsers: () => api.get<UserOut[]>("/api/users").then((r) => r.data),
  updateUser: (id: number, body: UserUpdate) =>
    api.patch<UserOut>(`/api/users/${id}`, body).then((r) => r.data),
  changePassword: (current_password: string, new_password: string) =>
    api.post("/api/auth/change_password", { current_password, new_password })
       .then((r) => r.data),

  // ── Clients ──
  clients: () => api.get<Client[]>("/api/clients").then((r) => r.data),

  // ── Plants ──
  plants: (params?: { client_id?: number; active_only?: boolean }) =>
    api.get<Plant[]>("/api/plants", { params }).then((r) => r.data),
  plant: (id: number) => api.get<Plant>(`/api/plants/${id}`).then((r) => r.data),
  // Integrity triage — the plot-pane fallback when no plot plan / PID exists.
  plantTriage: (id: number) =>
    api.get<PlantTriage>(`/api/plants/${id}/triage`).then((r) => r.data),
  // Global search — jump to any plant / asset / CML across the tenant.
  search: (q: string) =>
    api.get<SearchOut>("/api/search", { params: { q } }).then((r) => r.data),
  // DOEB / DIW regulatory compliance — inspection due-dates per plant.
  plantCompliance: (id: number) =>
    api.get<ComplianceOut>(`/api/plants/${id}/compliance`).then((r) => r.data),
  seedComplianceDefaults: (id: number) =>
    api.post<{ seeded: number; total: number }>(`/api/plants/${id}/compliance/seed-defaults`).then((r) => r.data),
  setRegInterval: (assetId: number, body: {
    interval_months?: number; confirm?: boolean; last_reg_inspection?: string;
    applicable_regulation?: string; reg_authority?: string;
  }) => api.patch<ComplianceRow>(`/api/assets/${assetId}/compliance`, body).then((r) => r.data),
  // ── Intake — the customer's in-portal upload drop-zone + operator file room ──
  intakeList: (params?: {
    status?: string; asset_id?: number; package_id?: number;
    direction?: IntakeDirection; current_only?: boolean;
    // Document-search filter row (2026-08-06 spec, task 6/7). Additive to the
    // params above — every existing caller keeps working unchanged.
    kind?: string; year?: string;
  }) => api.get<IntakeFile[]>("/api/intake", { params }).then((r) => r.data),
  // Facet counts for the filter row: which kinds/years actually have documents,
  // and how many are undated (the `year=none` bucket `intakeList` accepts).
  intakeFacets: () =>
    api.get<{ years: number[]; undated: number; kinds: Record<string, number> }>(
      "/api/intake/facets").then((r) => r.data),
  intakeFileToAsset: (id: number, body: { asset_id: number | null; kind?: string }) =>
    api.patch<IntakeFile>(`/api/intake/${id}/file`, body).then((r) => r.data),
  // `document_date` is the date printed ON the document, distinct from
  // uploaded_at. Pass "YYYY-MM-DD" to set it, null to clear it, and omit the
  // key entirely to leave it alone (the server keys off presence, not value).
  // It is NEVER derived here — no filename parsing, no upload-date fallback.
  intakePatch: (id: number, body: { filename?: string; note?: string; document_date?: string | null }) =>
    api.patch<IntakeFile>(`/api/intake/${id}`, body).then((r) => r.data),
  // The same date across a selection: historical documents arrive as "the 2019
  // pile", and a column that can only be filled one row at a time stays empty.
  intakeSetDates: (body: { file_ids: number[]; document_date: string | null }) =>
    api.post<{ updated: number }>("/api/intake/dates", body).then((r) => r.data),
  // Document control (workbench roadmap B) — file into the numbering tree,
  // independent of asset routing above; package_id=null unfiles it.
  intakeFileToPackage: (id: number, body: { package_id: number | null; doc_no?: string | null; direction?: IntakeDirection | null }) =>
    api.patch<IntakeFile>(`/api/intake/${id}/package`, body).then((r) => r.data),
  // Issue a new revision (staff): multipart, same shape as intakeUpload. The
  // old file's revision (if a plain int) auto-increments; otherwise pass an
  // explicit `revision`.
  intakeRevise: (id: number, file: File, revision?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (revision) form.append("revision", revision);
    return api
      .post<IntakeFile>(`/api/intake/${id}/revise`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  // Flagship-chat §D: save/clear a photo's markup. annotation is the
  // JSON-ENCODED shape array (JSON.stringify(shapes)); null clears it.
  saveAnnotation: (id: number, annotation: string | null) =>
    api.patch<IntakeFile>(`/api/intake/${id}/annotation`, { annotation }).then((r) => r.data),
  intakeRestore: (id: number) =>
    api.post<IntakeFile>(`/api/intake/${id}/restore`).then((r) => r.data),
  intakePurge: (id: number) =>
    api.delete<{ ok: boolean }>(`/api/intake/${id}/purge`).then((r) => r.data),
  intakeInspect: (id: number, params?: { sheet?: string; header_row?: number }) =>
    api.get<IntakeInspect>(`/api/intake/${id}/inspect`, { params }).then((r) => r.data),
  intakeImportPreview: (id: number, body: IntakeImportMapping) =>
    api.post<IntakeImportPreview>(`/api/intake/${id}/import-preview`, body).then((r) => r.data),
  intakeImportCommit: (id: number, body: IntakeImportMapping) =>
    api.post<IntakeImportResult>(`/api/intake/${id}/import-commit`, body).then((r) => r.data),
  intakeRegisterPreview: (id: number, body: RegisterMapping) =>
    api.post<RegisterPreview>(`/api/intake/${id}/register-preview`, body).then((r) => r.data),
  intakeRegisterCommit: (id: number, body: RegisterMapping) =>
    api.post<RegisterResult>(`/api/intake/${id}/register-commit`, body).then((r) => r.data),
  // Saved column-mapping templates for the ACTIVE tenant (staff must have a
  // tenant selected — the interceptor only sends X-Tenant-Id then).
  intakeTemplates: () =>
    api.get<IntakeTemplate[]>("/api/intake/templates").then((r) => r.data),
  intakeUpload: (file: File, note: string, onProgress?: (pct: number) => void, signal?: AbortSignal,
                 channel?: "chat") => {
    const form = new FormData();
    form.append("file", file);
    form.append("note", note);
    if (channel) form.append("channel", channel);
    return api
      .post<IntakeFile>("/api/intake", form, {
        headers: { "Content-Type": "multipart/form-data" },
        signal,
        onUploadProgress: (e) => {
          if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        },
      })
      .then((r) => r.data);
  },
  intakeDelete: (id: number) =>
    api.delete<{ ok: boolean }>(`/api/intake/${id}`).then((r) => r.data),

  // ── Document packages — the numbering tree (workbench roadmap B1) ──
  packages: () => api.get<Package[]>("/api/packages").then((r) => r.data),
  packageCreate: (body: PackageCreate) =>
    api.post<Package>("/api/packages", body).then((r) => r.data),
  packagePatch: (id: number, body: PackagePatch) =>
    api.patch<Package>(`/api/packages/${id}`, body).then((r) => r.data),
  packageDelete: (id: number) =>
    api.delete<{ ok: boolean }>(`/api/packages/${id}`).then((r) => r.data),

  // ── Submissions — client ⇄ team work handoff ──
  submissions: (params?: { status?: string }) =>
    api.get<Submission[]>("/api/submissions", { params }).then((r) => r.data),
  submission: (id: number) =>
    api.get<SubmissionDetail>(`/api/submissions/${id}`).then((r) => r.data),
  submissionCreate: (body: {
    title: string; description?: string; intent?: string; plant_id?: number;
    priority?: string; due_at?: string; file_ids?: number[]; submit?: boolean;
  }) => api.post<SubmissionDetail>("/api/submissions", body).then((r) => r.data),
  submissionTransition: (id: number, body: { to: string; outcome?: string; assigned_to_id?: number }) =>
    api.post<SubmissionDetail>(`/api/submissions/${id}/transition`, body).then((r) => r.data),
  submissionFiles: (id: number, body: { file_ids: number[]; action: "attach" | "detach" }) =>
    api.post<SubmissionDetail>(`/api/submissions/${id}/files`, body).then((r) => r.data),
  submissionPatch: (id: number, body: { assigned_to_id?: number; clear_assignee?: boolean; due_at?: string; clear_due?: boolean; priority?: string }) =>
    api.patch<SubmissionDetail>(`/api/submissions/${id}`, body).then((r) => r.data),
  submissionAssignees: () =>
    api.get<{ id: number; name: string }[]>("/api/submissions/assignees").then((r) => r.data),
  submissionEvents: (id: number) =>
    api.get<SubmissionEvent[]>(`/api/submissions/${id}/events`).then((r) => r.data),
  submissionPostMessage: (id: number, body: { body: string; is_internal?: boolean }) =>
    api.post<SubmissionEvent>(`/api/submissions/${id}/messages`, body).then((r) => r.data),
  submissionConversation: () =>
    api.get<Conversation>("/api/submissions/conversation").then((r) => r.data),
  submissionPostConversationMessage: (body: { body: string; is_internal?: boolean; reply_to?: number }) =>
    api.post<SubmissionEvent>("/api/submissions/conversation/message", body).then((r) => r.data),
  /** Read-receipt watermark (roadmap 2.2) — forward-only on the server, so a
   *  caller may fire this freely; the backend no-ops a stale/duplicate id. */
  submissionMarkConversationSeen: (lastEventId: number) =>
    api.post<{ last_seen_event_id: number }>("/api/submissions/conversation/seen",
      { last_event_id: lastEventId }).then((r) => r.data),
  // The two honest entry points that promote a บทสนทนา into a งาน — see
  // docs/superpowers/specs/2026-07-21-inquiry-vs-work.md. Both are body-less
  // POSTs; the backend reuses an in-window open delivery to avoid dupes.
  /** Customer (or staff on their behalf): "ขอให้ตรวจ/ประเมิน". */
  submissionRequestWork: () =>
    api.post<Submission>("/api/submissions/conversation/request-work").then((r) => r.data),
  /** Staff only: "รับเป็นงาน" — opens + self-assigns. */
  submissionAcceptAsWork: () =>
    api.post<Submission>("/api/submissions/conversation/accept-as-work").then((r) => r.data),
  /** Customer-only "อนุมัติรายงาน" (roadmap 3.2) — the approve gate on top of
   *  the Review Room. Soft gate: never required for/blocking completion. */
  submissionApprove: (id: number) =>
    api.post<SubmissionDetail>(`/api/submissions/${id}/approve`).then((r) => r.data),
  /** Staff-only "ตราเป็นบันทึกการตัดสินใจ" (roadmap 3.3, decision stamps) —
   *  marks a plain, non-internal thread message as an immutable decision
   *  record. No un-stamp call exists — the backend has none, by design. */
  submissionStampDecision: (eventId: number) =>
    api.post<SubmissionEvent>(`/api/submissions/conversation/events/${eventId}/stamp-decision`).then((r) => r.data),

  // ── The staff pipeline, as one list (2026-09-01 spec: the queue) ──
  /** GET /api/queue — one call, and it is the whole QUEUE screen: the rows,
   *  the split by whose move it is, every sentence a row prints, and the
   *  counts. Deliberately ONE call: the screen it replaces composed itself
   *  from three endpoints and could filter only one of them. */
  queue: () => api.get<QueueOut>("/api/queue").then((r) => r.data),
  /** POST /api/submissions/{id}/advance — the queue row's verb, fired.
   *
   *  `to_stage` is the one the server put on the row's `action`; it is NOT
   *  chosen here. The route asks `state.can_advance_stage` and
   *  `can_enter_stage` about the job, the same two functions the queue asked
   *  before it offered the button, so the browser decides nothing about who
   *  may move a job — it only reports which button was pressed. */
  submissionAdvance: (id: number, toStage: string) =>
    api.post<SubmissionDetail>(`/api/submissions/${id}/advance`,
                               { to_stage: toStage }).then((r) => r.data),

  // ── Cross-tenant staff inbox (2026-07-23 spec, PR 1) ──
  /** Staff-only triage list — one row per tenant with activity, sorted
   *  awaiting-reply-oldest-first then recency (server-side). */
  inbox: () => api.get<InboxRow[]>("/api/inbox").then((r) => r.data),

  // ── Presence — honest, heartbeat-backed (roadmap 2.3) ──
  /** Live presence in the caller's OWN tenant, excluding the caller. Used
   *  by TeamChatBody's presence strip; the periodic heartbeat itself lives
   *  in portal/src/lib/presence.ts (a raw axios/fetch call, not this one —
   *  see that file for why the pagehide leave call bypasses axios). */
  presence: () => api.get<PresenceEntry[]>("/api/presence").then((r) => r.data),
  presenceBeat: (body: { page: string; label: string }) =>
    api.post<{ ok: boolean }>("/api/presence/beat", body).then((r) => r.data),

  // ── Report Review Room — comment pins on delivered report PDFs (roadmap 3.1) ──
  reviewPins: (fileId: number) =>
    api.get<ReviewPinsOut>(`/api/review/${fileId}/pins`).then((r) => r.data),
  reviewCreatePin: (fileId: number, body: { page: number; x_pct: number; y_pct: number; body: string }) =>
    api.post<ReviewPin>(`/api/review/${fileId}/pins`, body).then((r) => r.data),
  reviewReplyToPin: (pinId: number, body: { body: string }) =>
    api.post<ReviewPin>(`/api/review/pins/${pinId}/reply`, body).then((r) => r.data),
  reviewResolvePin: (pinId: number) =>
    api.post<ReviewPin>(`/api/review/pins/${pinId}/resolve`).then((r) => r.data),
  reviewUnresolvePin: (pinId: number) =>
    api.post<ReviewPin>(`/api/review/pins/${pinId}/unresolve`).then((r) => r.data),

  // ── Notifications ──
  notifications: (unreadOnly?: boolean) =>
    api.get<AppNotification[]>("/api/notifications", { params: unreadOnly ? { unread_only: true } : undefined }).then((r) => r.data),
  notificationsUnread: () =>
    api.get<{ count: number }>("/api/notifications/unread-count").then((r) => r.data),
  notificationRead: (id: number) =>
    api.post(`/api/notifications/${id}/read`).then((r) => r.data),
  notificationsReadAll: () =>
    api.post("/api/notifications/read-all").then((r) => r.data),

  // Raw-water pipeline segments (straight geographic lines between plant nodes)
  // for the Atlas polylines. Empty unless the client has a defined network.
  pipelineNetwork: (clientId: number) =>
    api
      .get<{ segments: PipelineSegment[] }>("/api/plants/pipeline-network", { params: { client_id: clientId } })
      .then((r) => r.data),

  // ── Areas ──
  areas: (params?: { plant_id?: number }) =>
    api.get<Area[]>("/api/areas", { params }).then((r) => r.data),
  area: (id: number) => api.get<Area>(`/api/areas/${id}`).then((r) => r.data),

  // ── Assets ──
  assets: (params?: {
    area_id?: number; plant_id?: number; client_id?: number;
    asset_class?: string; include_retired?: boolean;
  }) => api.get<Asset[]>("/api/assets", { params }).then((r) => r.data),
  asset: (id: number) => api.get<Asset>(`/api/assets/${id}`).then((r) => r.data),
  /** Compact per-asset summary looked up BY TAG — the flagship-chat
   *  [[asset:TAG]] live card. worst_cml is populated server-side only for
   *  a staff caller (role-aware, not a client-side filter). */
  assetSummary: (tag: string) =>
    api.get<AssetSummary>("/api/assets/summary", { params: { tag } }).then((r) => r.data),
  /** Per-CML corrosion analysis + governing rollup (deterministic engine). */
  assetCorrosion: (ref: number | string) =>
    api.get<CorrosionData>(`/api/assets/${ref}/corrosion`).then((r) => r.data),
  /** The asset's integrity RECORD — every inspection date with its methods,
   *  findings and files, the rate summary with the sentence explaining each
   *  number, EVERY interval and due candidate with its provenance, the
   *  governing t-min and component, and the per-component rates.
   *
   *  `server/routers/integrity.py`. Distinct from `assetCorrosion` on purpose:
   *  that one is the per-CML engine and its rollup, this one is the asset's
   *  history at the grain the staff asset screen reads (one row per inspection
   *  DATE, not per technique). Nothing in the browser re-derives any of it —
   *  see `screens/asset/types.ts`, which is this response field-for-field. */
  assetIntegrity: (id: number) =>
    api.get<AssetIntegrity>(`/api/assets/${id}/integrity`).then((r) => r.data),
  /** RBI — same source the staff Review Studio's RBI tab uses (risk · PoF · CoF ·
   *  component register · corrosion loops · inspection coverage). */
  assetRbi: (ref: number | string) =>
    api.get<AssetRbi>(`/api/assets/${ref}/rbi`).then((r) => r.data),
  /** PREMIUM — action recommendation (402 if the tenant isn't entitled). */
  assetRecommendation: (ref: number | string) =>
    api.get<AssetRecommendation>(`/api/assets/${ref}/recommendation`).then((r) => r.data),

  // ── GVI findings + reference photos (free) ──
  // walkdowns = the authoritative GVI signal (a generic inspection labelled
  // "GVI" is NOT a GVI walkdown). 0 walkdowns ⇒ no GVI on record.
  assetFindings: (id: number) =>
    api.get<{ findings: AssetFinding[]; walkdowns?: string[] }>(`/api/assets/${id}/findings-overview`)
       .then((r) => ({ findings: r.data.findings ?? [], walkdowns: r.data.walkdowns ?? [] })),
  assetPhotos: (id: number) =>
    api.get<{ photos: AssetPhotoRef[] }>(`/api/assets/${id}/photos`).then((r) => r.data.photos ?? []),
  /** Fetch a reference photo as an object URL (auth + tenant header via axios). */
  assetPhotoUrl: (id: number, idx: number | string) =>
    api.get(`/api/assets/${id}/photos/${idx}`, { responseType: "blob" })
       .then((r) => URL.createObjectURL(r.data as Blob)),

  // ── Human-expert review overlay ──
  assetReview: (id: number) =>
    api.get<AssetReview>(`/api/assets/${id}/review`).then((r) => r.data),
  requestReview: (id: number) =>
    api.post<AssetReview>(`/api/assets/${id}/review/request`).then((r) => r.data),
  verifyReview: (id: number, body: { verdict: string; expert_notes: string; cert_ref: string; assessment?: AssetAssessment }) =>
    api.post<AssetReview>(`/api/assets/${id}/review/verify`, body).then((r) => r.data),
  claimReview: (id: number) =>
    api.post<AssetReview>(`/api/assets/${id}/review/claim`).then((r) => r.data),
  pendingReviews: () =>
    api.get<{ items: PendingReview[] }>(`/api/reviews/pending`).then((r) => r.data.items),

  // ── Entitlements (freemium gate) ──
  entitlements: () => api.get<Entitlements>("/api/entitlements").then((r) => r.data),
  /** Fetch the asset's STL as raw bytes. Throws on 404 (no STL). */
  assetStl: (id: number) =>
    api.get<ArrayBuffer>(`/api/assets/${id}/stl`, { responseType: "arraybuffer" })
       .then((r) => r.data),

  /** Fetch an area's plot-plan PNG as raw bytes (auth + tenant header via axios).
   *  A plain <img src=...> cannot send the Bearer JWT, so we must go through axios. */
  areaPlotPlan: (id: number) =>
    api.get<ArrayBuffer>(`/api/areas/${id}/plot-plan`, { responseType: "arraybuffer" })
       .then((r) => r.data),

  // ── CMLs ──
  cmls: (params?: { asset_id?: number }) =>
    api.get<CmlPoint[]>("/api/cml-points", { params }).then((r) => r.data),

  // ── Inspections ──
  inspections: (params?: {
    asset_id?: number; project_id?: number; job_id?: number;
    technique?: string; q?: string;
  }) => api.get<Inspection[]>("/api/inspections", { params }).then((r) => r.data),
  inspection: (id: number) =>
    api.get<Inspection>(`/api/inspections/${id}`).then((r) => r.data),
  /** Per-asset inspection log with per-inspection outcome (free). */
  assetInspectionHistory: (id: number) =>
    api.get<{ items: InspectionHistoryItem[] }>(`/api/assets/${id}/inspection-history`).then((r) => r.data.items),
  /** Raw readings captured in one inspection (the customer's own data — free). */
  measurements: (params?: { inspection_id?: number }) =>
    api.get<Measurement[]>("/api/measurements", { params }).then((r) => r.data),
  /** Download the original source Excel this inspection was imported from.
   *  Fetched as an auth'd blob (an <a href> can't send the JWT). Throws on
   *  404/503 when no raw source is on this host. */
  inspectionRawExcel: (id: number) =>
    api.get(`/api/inspections/${id}/raw-excel`, { responseType: "blob" }).then((r) => {
      const cd = String((r.headers as Record<string, string>)["content-disposition"] || "");
      const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      return { blob: r.data as Blob, filename: m ? decodeURIComponent(m[1]) : "" };
    }),

  // ── Workbench (roadmap C) ──
  /** Live what-if corrosion calculator — no persistence; the caller already
   *  has the CML's history client-side (from assetCorrosion). Same kernel
   *  the real /corrosion analysis uses (server/routers/calc.py). */
  whatIf: (body: WhatIfBody) =>
    api.post<WhatIfResult>("/api/calc/what-if", body).then((r) => r.data),
  /** Staff-only focus scratchpad (server/routers/notes.py). GET on a focus
   *  with no note yet returns an empty note, not 404. */
  focusNote: (focusType: string, focusRef: string) =>
    api.get<FocusNote>("/api/notes", { params: { focus_type: focusType, focus_ref: focusRef } })
       .then((r) => r.data),
  saveFocusNote: (focusType: string, focusRef: string, body: string) =>
    api.put<FocusNote>("/api/notes", { body }, { params: { focus_type: focusType, focus_ref: focusRef } })
       .then((r) => r.data),

  // ── Desk landing — estate strip + "return to your desk" (recent assets) ──
  /** recentIds = the browser-side recently-opened list (Task 5's storage);
   *  the server folds them into `recent` alongside its own signal. Omitted
   *  entirely (not sent as an empty string) when there is nothing to send. */
  deskSummary: (recentIds: number[]) =>
    api.get<DeskSummary>("/api/desk/summary", {
      params: recentIds.length ? { recent: recentIds.join(",") } : undefined,
    }).then((r) => r.data),

  // ── Guest access — staff invite management (2026-07-23 spec, PR 2) ──
  // Normal auth (STAFF_ROLES), same `api` instance as everything above —
  // these are NOT guest-reachable calls.
  guestCreateInvite: (body: GuestInviteCreate) =>
    api.post<GuestInvite>("/api/guest-invites", body).then((r) => r.data),
  guestListInvites: () =>
    api.get<GuestInvite[]>("/api/guest-invites").then((r) => r.data),
  guestRevokeInvite: (id: number) =>
    api.post<GuestInvite>(`/api/guest-invites/${id}/revoke`).then((r) => r.data),
};

// ── Guest access — public redeem + guest-scoped reads ───────────────
// Everything below goes through `guestApi` (the separate, guest-only axios
// instance above), never `api` — see that instance's comment for why.
export const guestClient = {
  /** Public — no auth. Trades the invite token for a short-lived guest JWT.
   *  The caller is responsible for storing the returned `token` via
   *  setGuestToken() before calling any of the read wrappers below. */
  redeem: (token: string) =>
    guestApi.post<GuestRedeemOut>("/api/guest/redeem", { token }).then((r) => r.data),
  /** Asset scope — the guest's own asset detail (403s on any other id). */
  asset: (assetId: number) =>
    guestApi.get<Asset>(`/api/guest/asset/${assetId}`).then((r) => r.data),
  assetCorrosion: (assetId: number) =>
    guestApi.get<CorrosionData>(`/api/guest/asset/${assetId}/corrosion`).then((r) => r.data),
  /** Files filed to the scoped asset — includes any certified report
   *  (kind="report") among them, same as the staff/customer file list. */
  assetFiles: (assetId: number) =>
    guestApi.get<IntakeFile[]>(`/api/guest/asset/${assetId}/files`).then((r) => r.data),
  /** Submission scope — current-revision files only (server-enforced). */
  submission: (subId: number) =>
    guestApi.get<SubmissionDetail>(`/api/guest/submission/${subId}`).then((r) => r.data),
  /** That submission's own thread only — never the tenant-wide conversation. */
  submissionEvents: (subId: number) =>
    guestApi.get<{ events: SubmissionEvent[] }>(`/api/guest/submission/${subId}/events`)
      .then((r) => r.data.events),
};

/** Trigger a browser download for an already-fetched blob.
 *  The anchor is attached before click() and removed after — matching the
 *  raw-excel download in InspectionDetailScreen; a detached anchor is not
 *  reliably clickable in every browser. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Download the tenant's full data bundle. Fetched as an auth'd blob because an
 *  <a href> cannot send the JWT — same reason as the raw-excel helper above. */
export async function downloadExportBundle(): Promise<void> {
  const r = await api.get("/api/export/bundle", { responseType: "blob" });
  const cd = String((r.headers as Record<string, string>)["content-disposition"] ?? "");
  const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? "xzynexa-export.zip";
  saveBlob(r.data as Blob, name);
}

/** Download one asset's CML table as an Excel workbook. */
export async function downloadCmlXlsx(assetId: number): Promise<void> {
  const r = await api.get(`/api/export/assets/${assetId}/cml-points.xlsx`,
                          { responseType: "blob" });
  saveBlob(r.data as Blob, `cml-points-asset${assetId}.xlsx`);
}

/** Download an intake file by id (team-dock FileChip, and anywhere else that
 *  only has the minimal {id, filename} shape used by FileChip — no presigned
 *  IntakeFile.download_url to fall back to). Fetched as an auth'd blob same
 *  as the other download* helpers above: the route is Bearer-gated, so a
 *  plain <a href> can't carry the JWT. */
export async function downloadIntakeFile(id: number, filename: string): Promise<void> {
  const r = await api.get(`/api/intake/${id}/download`, { responseType: "blob" });
  saveBlob(r.data as Blob, filename || "ไฟล์");
}

/** Fetch an intake file's bytes as a Blob WITHOUT triggering a save-as — for
 *  inline display (the photo thumbnail / markup modal in TeamChatDock). Same
 *  auth'd route as downloadIntakeFile; the caller turns this into an object
 *  URL and is responsible for revoking it. */
export async function fetchIntakeFileBlob(id: number): Promise<Blob> {
  const r = await api.get(`/api/intake/${id}/download`, { responseType: "blob" });
  return r.data as Blob;
}

/** A client budget must always EXCEED the server's, or we abort a render that is
 *  still working and throw away the result we asked for. The server's own
 *  ceiling is NAV_TIMEOUT_MS (15s) + RENDER_TIMEOUT_MS (30s) = 45s — both in
 *  `server/reports/pdf.py`. This is that, plus headroom for the transfer.
 *  CHANGE BOTH TOGETHER. The 15s global default (see api.create above) is far
 *  too short for this one call and is deliberately not raised for everything. */
const REPORT_PDF_TIMEOUT_MS = 60_000;

/** Download an inspection's report as a PDF (rendered server-side by chromium).
 *  Can take a few seconds — the caller should show progress. */
export async function downloadReportPdf(inspectionId: number): Promise<void> {
  const r = await api.get(`/api/inspections/${inspectionId}/report.pdf`,
                          { responseType: "blob",
                            timeout: REPORT_PDF_TIMEOUT_MS });
  saveBlob(r.data as Blob, `inspection-${inspectionId}.pdf`);
}

/** Same budget reasoning as REPORT_PDF_TIMEOUT_MS above, but the server-side
 *  ceiling here is much lower: `render_html_to_pdf` (server/reports/pdf.py)
 *  does a plain `page.set_content` on already-built HTML — no URL
 *  navigation, no client JS, no STL snapshot to wait on — so NAV_TIMEOUT_MS
 *  (15s) is the effective ceiling, not the full 45s report.pdf budget. Kept
 *  generous anyway for a slow connection's transfer time. */
const CONVERSATION_PDF_TIMEOUT_MS = 30_000;

/** Download the client<->team conversation + review-cycle history as a PDF
 *  appendix (roadmap 3.3 decision-stamps + export). Same auth'd-blob
 *  pattern as downloadReportPdf — an <a href> can't carry the JWT. Both
 *  staff and the customer may call this; the document itself excludes
 *  internal notes unconditionally (server/exports/conversation_pdf.py). */
export async function downloadConversationPdf(): Promise<void> {
  const r = await api.get("/api/submissions/conversation/export.pdf",
                          { responseType: "blob", timeout: CONVERSATION_PDF_TIMEOUT_MS });
  const cd = String((r.headers as Record<string, string>)["content-disposition"] ?? "");
  const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? "xzynexa-conversation.pdf";
  saveBlob(r.data as Blob, name);
}

/** Extract a useful human-readable error message from an axios error. */
export function errMsg(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const detail = e.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d.msg ?? String(d)).join("; ");
    // Structured 400/409 details (e.g. packages.delete_package's
    // {message, child_count, file_count}, intake import-commit's
    // {message, db_conflicts}) — surface the human-readable message.
    if (detail && typeof detail === "object" && typeof detail.message === "string") return detail.message;
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}
